import React from 'react';

import { cn, withRef } from '@udecode/cn';
import { withHOC } from '@udecode/plate-common/react';
import { Image, ImagePlugin, useMediaState } from '@udecode/plate-media/react';
import { ResizableProvider, useResizableStore } from '@udecode/plate-resizable';

import { Caption, CaptionTextarea } from './caption';
import { MediaPopover } from './media-popover';
import { PlateElement } from './plate-element';
import {
  Resizable,
  ResizeHandle,
  mediaResizeHandleVariants,
} from './resizable';

export const ImageElement = withHOC(
  ResizableProvider,
  withRef<typeof PlateElement>(
    ({ children, className, nodeProps, ...props }, ref) => {
      const { align = 'center', focused, readOnly, selected } = useMediaState();
      const width = useResizableStore().get.width();

      return (
        <MediaPopover plugin={ImagePlugin}>
          <PlateElement
            ref={ref}
            className={cn('py-2.5', className)}
            {...props}
          >
            <figure className="group relative m-0" contentEditable={false}>
              <Resizable
                align={align}
                options={{
                  align,
                  readOnly,
                  maxWidth: '90vw',
                }}
              >
                {/* Left Handle */}
                <ResizeHandle
                  options={{ direction: 'left' }}
                  className={cn(
                    mediaResizeHandleVariants({ direction: 'left' }),
                    'opacity-0 group-hover:opacity-100',
                    focused && selected && 'opacity-100'
                  )}
                />

                <Image
                  className={cn(
                    'block w-full max-w-full cursor-pointer object-cover px-0',
                    'rounded-sm',
                    focused && selected && 'ring-2 ring-ring ring-offset-2'
                  )}
                  alt=""
                  {...nodeProps}
                />

                {/* Right Handle */}
                <ResizeHandle
                  options={{ direction: 'right' }}
                  className={cn(
                    mediaResizeHandleVariants({ direction: 'right' }),
                    'opacity-0 group-hover:opacity-100',
                    focused && selected && 'opacity-100'
                  )}
                />

                {/* NEW: Bottom-right corner handle */}
                <ResizeHandle
                  options={{ direction: 'right' }}
                  className={cn(
                    mediaResizeHandleVariants({ direction: 'right' }),
                    'opacity-0 group-hover:opacity-100',
                    focused && selected && 'opacity-100'
                  )}
                />
              </Resizable>

              <Caption style={{ width }} align={align}>
                <CaptionTextarea
                  readOnly={readOnly}
                  placeholder="Write a caption..."
                />
              </Caption>
            </figure>

            {children}
          </PlateElement>
        </MediaPopover>
      );
    }
  )
);